import { CONSTANTS } from 'config/constants';
import moment from 'moment';
import 'moment/min/locales';
import { formatDate, getCurrentLocaleFormat } from 'utils/format';
import type { DateVariant } from 'utils/format';
import { LocaleLanguage } from 'models/locale-format';

const MOMENT_LOCALE: Record<LocaleLanguage, string> = {
    [LocaleLanguage.EN_US]: 'en',
    [LocaleLanguage.SV_SE]: 'sv',
    [LocaleLanguage.NO_NO]: 'nb',
    [LocaleLanguage.DA_DK]: 'da',
    [LocaleLanguage.FI_FI]: 'fi',
    [LocaleLanguage.DEBUG]: 'en',
};

const { HOLIDAYS, TRANSPORT_TIME } = CONSTANTS;

class TransportDate {
    private static _instance: TransportDate = new TransportDate();

    constructor() {
        if (TransportDate._instance) {
            throw new Error('Singleton class');
        }
        TransportDate._instance = this;
    }

    public static get instance(): TransportDate {
        return TransportDate._instance;
    }

    nextWorkingDay(selectedDate: Date | null, searchForward = true) {
        let day,
            holidayIndex,
            nextDate = moment(selectedDate);
        do {
            if (searchForward) {
                nextDate = moment(nextDate).add(1, 'days');
            } else {
                if (!moment(selectedDate).isSame(new Date().setHours(0, 0, 0, 0))) {
                    nextDate = moment(nextDate).subtract(1, 'days');
                }
            }
            day = nextDate.toDate().getDay();
            const latestDeliveryDateMilliseconds = nextDate.toDate().setHours(0, 0, 0, 0);
            holidayIndex = HOLIDAYS.indexOf(latestDeliveryDateMilliseconds);
        } while (day === 0 || day === 6 || holidayIndex > -1);
        return nextDate.toDate();
    }

    nextWorkingDayAsString(selectedDate: Date | null, searchForward = true) {
        return moment(this.nextWorkingDay(selectedDate, searchForward)).format('YYYY-MM-DD');
    }

    transportDateFormatter(date: string, variant: DateVariant = 'date') {
        if (!date) {
            return date;
        }
        const transportDate = new Date(date).setHours(0, 0, 0, 0);
        const todayMilliseconds = new Date().setHours(0, 0, 0, 0);
        const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
        const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
        const tomorrowMilliseconds = new Date(tomorrow).setHours(0, 0, 0, 0);
        const yesterdayMilliseconds = new Date(yesterday).setHours(0, 0, 0, 0);
        if (transportDate === todayMilliseconds) {
            return 'TODAY';
        }
        if (transportDate === tomorrowMilliseconds) {
            return 'TOMORROW';
        }
        if (transportDate === yesterdayMilliseconds) {
            return 'YESTERDAY';
        }
        moment.locale(MOMENT_LOCALE[getCurrentLocaleFormat().language]);
        const shortWeekDay = moment(date).format('ddd');
        return `${shortWeekDay}, ${formatDate(date, variant)}`;
    }

    transportDisabledDates(date: Date) {
        if (date.getDay() === 6 || date.getDay() === 0) {
            return true;
        }
        return HOLIDAYS.some((holiday) => {
            return holiday === date.setHours(0, 0, 0, 0);
        });
    }

    isPickupDisabled(transportDate: Date): boolean {
        return this.isTransportDisabled(transportDate, TRANSPORT_TIME.PICKUP, false);
    }

    isDeliveryDisabled(transportDate: Date): boolean {
        const date = this.nextWorkingDay(transportDate, false);
        return this.isTransportDisabled(date, TRANSPORT_TIME.DELIVERY, true);
    }

    isPickupReadOnly(transportDate: Date): boolean {
        return this.isTransportDisabled(transportDate, TRANSPORT_TIME.PICKUP, false);
    }

    isDeliveryReadOnly(transportDate: Date): boolean {
        return this.isTransportDisabled(transportDate, TRANSPORT_TIME.DELIVERY, true);
    }

    isSameDayDelivery(transportDate: Date | null) {
        if (!transportDate) {
            return false;
        }

        return (
            transportDate.getDate() === new Date().getDate() &&
            transportDate.getMonth() === new Date().getMonth() &&
            transportDate.getFullYear() === new Date().getFullYear()
        );
    }

    private isTransportDisabled(selectedDate: Date, deadlineHour: number, isDelivery: boolean): boolean {
        const date = moment(selectedDate);
        if (date.startOf('day').isBefore(moment().startOf('day'))) {
            return true;
        }
        if (date.startOf('day').isSame(moment().startOf('day'))) {
            // selected date is today, check for time
            if (isDelivery) {
                const time = parseFloat(`${new Date().getHours()}.${new Date().getMinutes()}`);
                return time >= deadlineHour;
            }
            return new Date().getHours() >= deadlineHour;
        }
        return false;
    }
}

export const transportDateHelper = TransportDate.instance;
